import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { GROUP_ORDER, colorFor, labelFor } from "./palette";

// SET UP THE MAP.
//
// The web is three hundred things and a thousand lines between them, and
// dropping somebody into the middle of it is not showing them anything. This
// asks the three questions that make it legible BEFORE the wandering starts:
// where to begin, what kinds of thing to let in at all, and how much of each
// one to draw at once.
//
// It is a native <dialog> on purpose. Focus trapping, Escape, the inert
// backdrop and the top-layer stacking are all things this would otherwise have
// to reimplement, and every hand-rolled modal in the world gets at least one of
// them wrong.
//
// The three parts are NOT numbered. They are all on one screen, and numbering
// them would make a wizard out of a form — the same rule the reading setup
// follows.

// Long enough that a word typed at speed is one search rather than five.
const DEBOUNCE_MS = 200;

// jsdom has neither showModal nor close (28.x), so the element's own open
// attribute is the fallback. It is a test-environment concession and nothing
// more: in a browser the real methods are always the ones taken, because they
// are what puts the dialog in the top layer.
function show(node) {
  if (!node) return;
  if (typeof node.showModal === "function") {
    if (!node.open) node.showModal();
  } else {
    node.setAttribute("open", "");
  }
}

function hide(node) {
  if (!node) return;
  if (typeof node.close === "function") {
    if (node.open) node.close();
  } else {
    node.removeAttribute("open");
  }
}

const countNodes = (groups) =>
  groups.reduce((total, group) => total + (group.nodes?.length || 0), 0);


// What a key was called, for the summary line of a folded section. The results
// are grouped, so the name is not lying about somewhere to look for it.
function findNode(groups, key) {
  for (const group of groups) {
    const found = group.nodes.find((node) => node.key === key);
    if (found) return found;
  }
  return null;
}

// ONE TOPIC, folded away until it is wanted.
//
// The three questions stacked out flat made a dialog taller than most windows,
// and a reader answering the first had to scroll past the other two to find
// the button. Folded, the box is three headings and an answer.
//
// A button with aria-expanded and aria-controls, NOT a `role="tablist"`: these
// are foldable sections and carry no arrow-key contract, and announcing one
// that is not implemented is worse than announcing nothing.
//
// The panel stays MOUNTED and uses `hidden`. It holds the reader's draft, and
// unmounting a closed section would throw that away — the same rule the
// chronology's layer panel follows, for the same reason.
function Part({ children, id, open, onOpen, summary, title }) {
  return (
    <section className={`setup__part${open ? " setup__part--open" : ""}`}>
      <h3 className="setup__heading">
        <button
          type="button"
          className="setup__disclose"
          aria-expanded={open}
          aria-controls={id}
          data-testid={`setup-open-${id.split("-").pop()}`}
          onClick={onOpen}
        >
          <span className="setup__disclose-title">{title}</span>
          {/* What is set, while it is shut. A fold that hides its own answer
              is worse than the section it replaced. */}
          <span className="setup__summary">{summary}</span>
        </button>
      </h3>
      <div className="setup__body" id={id} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

export default function SetupModal({
  kindCounts,
  kinds,
  onApply,
  onClose,
  open,
  perBranch,
  perBranchChoices,
  search
}) {
  const dialog = useRef(null);
  const headingId = useId();
  const searchId = useId();
  const perBranchId = useId();

  // DRAFT state. Nothing here reaches the map until the reader confirms, so
  // cancelling — by button, by Escape, by the backdrop — costs them nothing.
  // Re-opening reads the props again, which is what makes that true.
  const [startKey, setStartKey] = useState(null);
  const [draftKinds, setDraftKinds] = useState(() => new Set(kinds));
  const [draftPerBranch, setDraftPerBranch] = useState(perBranch);

  // One at a time. Independent folds would still stack into a tall box, and
  // the point of this was the scrolling.
  const [openPart, setOpenPart] = useState("start");

  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [suggested, setSuggested] = useState(true);
  // idle | searching | ready | failed. Never a bare empty list: an empty list
  // and a search that has not answered yet look identical, and only one of
  // them means "nothing matches".
  const [status, setStatus] = useState("idle");

  // The caller hands us `search` inline more often than not, so it is a fresh
  // function on every render. Reading it through a ref keeps it out of the
  // effect's dependencies — with it in there, every answer would re-render,
  // which would make a new `search`, which would search again, for ever.
  const searchRef = useRef(search);
  useEffect(() => { searchRef.current = search; });

  // Which request the answer on the wire belongs to. A slow search for "sat"
  // must not overwrite the fast one for "saturn".
  const ticketRef = useRef(0);

  const run = useCallback((term) => {
    const ticket = ticketRef.current + 1;
    ticketRef.current = ticket;
    setStatus("searching");
    Promise.resolve()
      .then(() => searchRef.current(term))
      .then((payload) => {
        if (ticket !== ticketRef.current) return;
        setGroups(Array.isArray(payload?.groups) ? payload.groups : []);
        setSuggested(payload?.suggested ?? term === "");
        setStatus("ready");
      })
      .catch(() => {
        if (ticket !== ticketRef.current) return;
        setStatus("failed");
      });
  }, []);

  // Opening resets the draft AND shows the dialog in one pass, so a re-opened
  // modal never paints a frame of the last visit's choices.
  //
  // `kinds` and `perBranch` are deliberately not dependencies: they are the
  // starting point for a visit, not a subscription. A change to them while the
  // modal is open belongs to the map behind it, and wiping the reader's draft
  // to match would be the map reaching in and editing the form.
  useEffect(() => {
    if (!open) {
      hide(dialog.current);
      return;
    }
    setStartKey(null);
    setDraftKinds(new Set(kinds));
    setOpenPart("start");
    setDraftPerBranch(perBranch);
    setQuery("");
    show(dialog.current);
    // Something to choose from immediately: the suggested starting points,
    // which is what an empty query asks for.
    run("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, run]);

  // Typing. The suggested list is not a search — there is no keystroke to wait
  // for — so only a real query is debounced, and the effect skips the first
  // pass after opening because the effect above has already asked for it.
  const opened = useRef(false);
  useEffect(() => {
    if (!open) {
      opened.current = false;
      return undefined;
    }
    if (!opened.current) {
      opened.current = true;
      return undefined;
    }
    const term = query.trim();
    if (term === "") {
      run("");
      return undefined;
    }
    const timer = setTimeout(() => run(term), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, query, run]);

  // Escape and the backdrop close the dialog themselves; this is how the host
  // finds out. The guard is for the other direction — when the host has
  // already set `open` to false, the close above fires this event too, and
  // telling it to close again would be an echo.
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const node = dialog.current;
    if (!node) return undefined;
    const onNativeClose = () => { if (openRef.current) onClose(); };
    node.addEventListener("close", onNativeClose);
    return () => node.removeEventListener("close", onNativeClose);
  }, [onClose]);

  const present = GROUP_ORDER.filter((group) => kindCounts[group]);
  const on = present.filter((group) => draftKinds.has(group));

  const toggleKind = (group) => {
    setDraftKinds((current) => {
      const next = new Set(current);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  };

  const apply = () => {
    onApply({ startKey, kinds: draftKinds, perBranch: draftPerBranch });
    onClose();
  };

  const shown = countNodes(groups);

  return (
    <dialog className="setup" ref={dialog} aria-labelledby={headingId} data-testid="setup-modal">
      {/* A div and not a form: there is nothing to submit, and a form inside
          a dialog either swallows Enter or closes on it without applying —
          both of which are a discard wearing a confirm's clothes. */}
      <div className="setup__form">
        <h2 className="setup__title" id={headingId}>Set up the map</h2>
        <p className="setup__lede">
          Start with one thing you want to understand. Then choose which kinds
          of things may appear and how many connections to show around each one.
          Nothing is removed from the catalogue; these choices only keep the
          first view readable.
        </p>

        <div className="setup__parts">
        <Part
          id={`${headingId}-start`}
          onOpen={() => setOpenPart(openPart === "start" ? null : "start")}
          open={openPart === "start"}
          summary={startKey
            ? (findNode(groups, startKey)?.name || "chosen")
            : "keep the current starting point"}
          title="Choose a starting point"
        >

          <label className="setup__field" htmlFor={searchId}>
            <span>Search for a thing to begin at</span>
            <input
              id={searchId}
              type="search"
              autoComplete="off"
              value={query}
              placeholder="Saturn, Binah, lead…"
              data-testid="setup-search"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>

          <p className="setup__hint">
            {suggested
              ? "These are well-connected starting points, with the planets first. Or type any name."
              : `Matches for “${query.trim()}”.`}
          </p>

          {status === "searching" ? (
            <p className="setup__status" role="status" data-testid="setup-searching">
              Looking…
            </p>
          ) : null}

          {status === "failed" ? (
            <p className="setup__status setup__status--no" role="status" data-testid="setup-search-failed">
              The search did not answer.{" "}
              <button type="button" className="setup__retry" onClick={() => run(query.trim())}>
                Try it again
              </button>
            </p>
          ) : null}

          {status === "ready" && shown === 0 ? (
            <p className="setup__status" data-testid="setup-no-matches">
              Nothing in this catalogue is called that. Try another spelling or
              a broader name.
            </p>
          ) : null}

          {groups.map((group) => (
            <div className="setup__group" key={group.group}>
              <h4 className="setup__group-label">
                <span className="setup__swatch" style={{ background: colorFor(group.group) }} aria-hidden="true" />
                {group.label || labelFor(group.group)}
                {group.total > (group.nodes?.length || 0) ? (
                  <span className="setup__tally">{group.nodes.length} of {group.total}</span>
                ) : null}
              </h4>
              <ul className="setup__results">
                {(group.nodes || []).map((node) => (
                  <li key={node.key}>
                    <button
                      type="button"
                      className={`setup__result${startKey === node.key ? " setup__result--on" : ""}`}
                      aria-pressed={startKey === node.key}
                      data-testid={`setup-result-${node.key}`}
                      onClick={() => setStartKey(node.key)}
                    >
                      <span className="setup__result-name">{node.name}</span>
                      <span className="setup__result-kind">{node.kind}</span>
                      <span className="setup__result-degree">
                        {node.connections} {node.connections === 1 ? "connection" : "connections"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </Part>

        <Part
          id={`${headingId}-kinds`}
          onOpen={() => setOpenPart(openPart === "kinds" ? null : "kinds")}
          open={openPart === "kinds"}
          summary={draftKinds.size === Object.keys(kindCounts).length
            ? "every kind"
            : `${draftKinds.size} of ${Object.keys(kindCounts).length} kinds`}
          title="Choose what may appear"
        >
          <p className="setup__hint">
            Untick a kind to hide it from this map. This is useful when long lists,
            such as plants or historical provinces, would crowd out the things
            you came to compare.
          </p>

          {/* Both stay mounted and go disabled. A control that vanishes when
              you use it drops a keyboard reader at the top of the document. */}
          <div className="setup__bulk">
            <button
              type="button"
              data-testid="setup-kinds-all"
              disabled={on.length === present.length}
              onClick={() => setDraftKinds(new Set(present))}
            >
              Include every kind
            </button>
            <button
              type="button"
              data-testid="setup-kinds-none"
              disabled={on.length === 0}
              onClick={() => setDraftKinds(new Set())}
            >
              Leave every kind out
            </button>
          </div>

          <ul className="setup__kinds">
            {present.map((group) => (
              <li key={group}>
                <label className="setup__kind">
                  <input
                    type="checkbox"
                    checked={draftKinds.has(group)}
                    data-testid={`setup-kind-${group}`}
                    onChange={() => toggleKind(group)}
                  />
                  <span className="setup__swatch" style={{ background: colorFor(group) }} aria-hidden="true" />
                  <span>{labelFor(group)}</span>
                  <span className="setup__tally">{kindCounts[group]}</span>
                </label>
              </li>
            ))}
          </ul>
        </Part>

        <Part
          id={`${headingId}-per`}
          onOpen={() => setOpenPart(openPart === "per" ? null : "per")}
          open={openPart === "per"}
          summary={draftPerBranch === null ? "show every connection" : `show ${draftPerBranch} first`}
          title="Choose how many connections to show"
        >

          <label className="setup__field" htmlFor={perBranchId}>
            <span>Connections shown around each thing</span>
            <select
              id={perBranchId}
              value={draftPerBranch === null ? "all" : String(draftPerBranch)}
              data-testid="setup-per-branch"
              onChange={(event) => setDraftPerBranch(
                event.target.value === "all" ? null : Number(event.target.value)
              )}
            >
              {perBranchChoices.map((choice) => (
                <option key={String(choice)} value={choice === null ? "all" : String(choice)}>
                  {choice === null ? "every connection" : `the first ${choice}`}
                </option>
              ))}
            </select>
          </label>

          <p className="setup__hint">
            This limit applies to each thing, not to the whole map. Foundational,
            older and multiply sourced links appear first; this is a reading
            order, not a score of what is true. A circle tells you when more
            connections are hidden, and one press reveals them.
          </p>
        </Part>

        </div>

        <div className="setup__actions">
          <button
            type="button"
            className="setup__cancel"
            data-testid="setup-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="setup__apply"
            data-testid="setup-apply"
            onClick={apply}
          >
            Draw the map
          </button>
        </div>
      </div>
    </dialog>
  );
}
